import { CARD_H, CARD_W } from "@webscan/constants.js";
import type { Printing } from "@webscan/catalogue.js";
import type { Candidate } from "@webscan/decide.js";
import { warn } from "./debug";

/**
 * Keepsake captures: the best frame of every logged card, kept in the browser.
 *
 * The find log's thumbnail is a 96×134 miniature; this stores the same warp at
 * twice the canonical raster (976x1360) as a JPEG — in memory for the row's
 * click-to-view, and in IndexedDB so a session's captures survive a reload. Storage is
 * best-effort throughout: a browser that refuses IndexedDB (private mode) costs the
 * persistence, never the scan.
 *
 * Shared between the two sides, as a store rather than a conversation: the scanner
 * writes a capture per logged read, the history panel lists them. Neither goes through
 * the other to do so.
 *
 * The store is also where an ambiguous read gets its answer. The scanner records the
 * read's shortlist with every capture; a review later lets the user pick the printing
 * and {@link resolveCapture} writes that choice onto the row. The scanner is never told
 * — its verdict stays AMBIGUOUS in the event it emitted — which is what keeps the user's
 * decision a decision and not a verdict the funnel did not reach.
 */

/**
 * Upscale of the keepsake capture over the canonical raster.
 *
 * Recognition stays at 488x680; the capture samples the same homography at twice the
 * density, which the sensor supports — a card fills 950-1900 px of it. Costs a
 * 976x1360 framebuffer (~5 MB) and one extra readback per *read*, not per frame.
 */
export const HQ_SCALE = 2;

/**
 * What the store knows a capture as: ACCEPTED and AMBIGUOUS are the scanner's word,
 * RESOLVED is the user's — an ambiguous read whose printing they have chosen.
 */
export type CaptureVerdict = "ACCEPTED" | "AMBIGUOUS" | "RESOLVED";

/**
 * Who turned an ambiguous read into a RESOLVED row: the user in a review, or the
 * price-tie policy at arrival (`shared/price-tie.ts`). Two different claims, labelled
 * differently everywhere a row is shown.
 */
export type ResolvedBy = "user" | "price";

/** A printing as the store identifies it; `key` is `setCode/collector/name`. */
export interface CaptureIdentity {
  key: string;
  name: string;
  setCode: string;
  collector: string;
}

/** One printing the read weighed, as a review offers it. */
export interface CaptureCandidate {
  name: string;
  setCode: string;
  collector: string;
  /** The candidate's calibrated score at read time, 0..1. */
  score: number;
}

/** What the scanner records with every logged read. */
export interface CaptureMeta extends CaptureIdentity {
  at: number;
  verdict: CaptureVerdict;
  /** The leader's artwork id — what lets a review group several scans of one picture. */
  illustration: string;
  /** The read's shortlist, best first — the choice a review offers. See {@link shortlist}. */
  candidates: CaptureCandidate[];
}

/** The scanner's reading of a row the user has since resolved: identity and verdict. */
export interface ScannedAs extends CaptureIdentity {
  verdict: CaptureVerdict;
}

/** The store's key for a printing — the one the scanner suppresses repeats by, too. */
export function captureKey(
  printing: Pick<CaptureIdentity, "name" | "setCode" | "collector">,
): string {
  return `${printing.setCode}/${printing.collector}/${printing.name}`;
}

function identity(
  printing: Pick<CaptureIdentity, "name" | "setCode" | "collector">,
): CaptureIdentity {
  return {
    key: captureKey(printing),
    name: printing.name,
    setCode: printing.setCode,
    collector: printing.collector,
  };
}

/**
 * How many of a read's candidates a capture keeps.
 *
 * `decide` sorts by score, and the printings a review has to choose between — the
 * siblings of one artwork, or the rival artwork that was too close to call — tie or
 * nearly tie with the leader, so they sit at the top. Anything ranked further down was
 * never the ambiguity. Every candidate that shares the leader's name is kept regardless,
 * because the question a review answers is "which printing of this card", and a review
 * that still needs more has the catalogue.
 */
export const SHORTLIST = 24;

/** The part of a read's candidate list a capture keeps, best first. */
export function shortlist(candidates: readonly Candidate[]): CaptureCandidate[] {
  const leader = candidates[0]?.printing.name;
  return candidates
    .filter((candidate, index) => index < SHORTLIST || candidate.printing.name === leader)
    .map(({ printing, score }) => ({
      name: printing.name,
      setCode: printing.setCode,
      collector: printing.collector,
      score,
    }));
}

/** Newest captures kept; older rows are pruned so the store cannot grow unbounded. */
const MAX_CAPTURES = 200;
const DB_NAME = "webscan2";
const STORE = "captures";

/**
 * Encode a warp as JPEG, rotated upright when the read was upside down.
 *
 * `scale` is the warp's density over the canonical raster — `HQ_SCALE` for the GPU
 * keepsake, 1 for the recognition raster the CPU fallback produces. A device without the
 * GL sampler would otherwise scan into an empty history, and half the density is still
 * the record of the scan.
 */
export function encodeCapture(
  pixels: Uint8ClampedArray,
  flipped: boolean,
  scale: number = HQ_SCALE,
): Promise<Blob | null> {
  const w = CARD_W * scale;
  const h = CARD_H * scale;
  return encodeRaster(pixels, w, h, flipped, w, h, 0.92);
}

/** Small upright JPEG for session rows. Persist the Blob, never a transient object URL. */
export function encodeThumbnail(pixels: Uint8ClampedArray, flipped: boolean): Promise<Blob | null> {
  return encodeRaster(pixels, CARD_W, CARD_H, flipped, 96, 134, 0.85);
}

/**
 * Borrow the owned scan raster for upload rather than cloning its RGBA bytes again.
 * putImageData copies into the native canvas synchronously; JPEG compression completes
 * asynchronously through toBlob. Only a shared-buffer caller needs a defensive copy
 * because ImageData cannot wrap SharedArrayBuffer (the scanner passes owned buffers).
 */
async function encodeRaster(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  flipped: boolean,
  outputWidth: number,
  outputHeight: number,
  quality: number,
): Promise<Blob | null> {
  if (
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width <= 0 ||
    height <= 0 ||
    pixels.length !== width * height * 4
  )
    return null;
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  let scratch: HTMLCanvasElement | null = null;
  try {
    const context = canvas.getContext("2d");
    if (!context) return null;
    const data =
      pixels.buffer instanceof ArrayBuffer
        ? (pixels as Uint8ClampedArray<ArrayBuffer>)
        : new Uint8ClampedArray(pixels);
    const image = new ImageData(data, width, height);
    if (flipped || outputWidth !== width || outputHeight !== height) {
      // putImageData ignores transforms; scaling/rotation use one temporary source.
      scratch = document.createElement("canvas");
      scratch.width = width;
      scratch.height = height;
      const sctx = scratch.getContext("2d");
      if (!sctx) return null;
      sctx.putImageData(image, 0, 0);
      context.imageSmoothingQuality = "high";
      if (flipped) {
        context.translate(outputWidth, outputHeight);
        context.rotate(Math.PI);
      }
      context.drawImage(scratch, 0, 0, outputWidth, outputHeight);
    } else {
      context.putImageData(image, 0, 0);
    }
    return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  } catch (error) {
    warn("capture", "could not encode scan image", error);
    return null;
  } finally {
    // Dropping the JS element does not promptly release its native backing store in
    // WebKit. Resizing to zero does, which keeps successive scan captures from stacking
    // several megabytes of IOSurface memory until Safari kills the tab.
    canvas.width = 0;
    canvas.height = 0;
    if (scratch) {
      scratch.width = 0;
      scratch.height = 0;
    }
  }
}

let database: Promise<IDBDatabase | null> | null = null;

/**
 * Why persistence is not working, once anything has proved that it is not.
 *
 * Set from whichever failure comes first and never overwritten — the first one is the
 * cause and the rest are its echo. The history view reads it, because "no scans yet" and
 * "this browser is refusing to keep your scans" are different things to tell someone who
 * has just scanned twenty cards.
 */
let storageIssue: string | null = null;

export function captureStorageIssue(): string | null {
  return storageIssue;
}

/** Record the first storage failure, in the browser's own words where it has any. */
function refused(what: string, error: unknown): void {
  const name = error instanceof DOMException ? error.name : null;
  const detail = name ?? (error instanceof Error ? error.message : null);
  const issue = `${what}${detail ? ` (${detail})` : ""}`;
  if (!storageIssue) {
    storageIssue = issue;
    // Once, not per scan: a browser that refuses the first write refuses every one.
    warn("boot", `scans are not being stored — ${issue}; keeping this session's in memory`);
  }
}

function openDb(): Promise<IDBDatabase | null> {
  database ??= new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        refused("this browser would not open its scan store", request.error);
        resolve(null);
      };
    } catch (error) {
      // Reached when `indexedDB` itself is missing or throws on access — some private
      // modes do exactly that. Silent here was the bug: nothing downstream could tell.
      refused("this browser has no usable scan storage", error);
      resolve(null);
    }
  });
  return database;
}

/** A persisted capture, as the history view reads it back. */
export interface StoredCapture extends CaptureMeta {
  id: number;
  blob: Blob;
  /**
   * What the scanner named the card before the user resolved it; null until then. Once
   * set, the row's own identity is the user's choice and this is the read's, kept so the
   * choice can be taken back and the record stays honest about who decided.
   */
  scannedAs: ScannedAs | null;
  /** Exact user choice, including online-only/language variants and their own prices. */
  chosenPrinting?: Printing | null;
  /** Who resolved a RESOLVED row. Absent on rows written before it existed: all the user's. */
  resolvedBy?: ResolvedBy | null;
}

/**
 * A row as it may sit in the database: captures written before the store carried a
 * shortlist lack the three newer fields, and read back with them empty.
 */
type StoredRow = Omit<StoredCapture, "illustration" | "candidates" | "scannedAs"> &
  Partial<Pick<StoredCapture, "illustration" | "candidates" | "scannedAs">>;

function fromStore(row: StoredRow): StoredCapture {
  return {
    ...row,
    illustration: row.illustration ?? "",
    candidates: row.candidates ?? [],
    scannedAs: row.scannedAs ?? null,
    chosenPrinting: row.chosenPrinting ?? null,
    resolvedBy: row.resolvedBy ?? null,
  };
}

/**
 * Captures a refusing browser gets to keep anyway, for as long as the tab lives.
 *
 * A private tab is a legitimate way to use this — someone trying the scanner out — and
 * "your scans vanished" is a much worse answer than "your scans last until you close the
 * tab". Bounded far below the persisted store because these sit in the tab's memory on a
 * phone already carrying the models: 40 JPEGs is some 14 MB, which is affordable where
 * 200 would not be. Ids are negative so a row's origin is legible everywhere it travels.
 */
const MAX_SESSION_CAPTURES = 40;
let sessionRows: StoredCapture[] = [];
let sessionId = 0;

function keepInSession(meta: CaptureMeta, blob: Blob): number {
  const id = --sessionId;
  sessionRows.unshift({ ...meta, id, blob, scannedAs: null });
  if (sessionRows.length > MAX_SESSION_CAPTURES) sessionRows.length = MAX_SESSION_CAPTURES;
  return id;
}

/**
 * Every capture the browser has, newest first — the session's own in front of the
 * persisted ones, whose walk is over descending auto-increment keys, the same order
 * pruning trusts.
 */
export async function loadCaptures(): Promise<StoredCapture[]> {
  const db = await openDb();
  if (!db) return [...sessionRows];
  const stored = await readStored(db);
  return sessionRows.length ? [...sessionRows, ...stored] : stored;
}

function readStored(db: IDBDatabase): Promise<StoredCapture[]> {
  return new Promise((resolve) => {
    const rows: StoredCapture[] = [];
    try {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).openCursor(null, "prev");
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(rows);
          return;
        }
        rows.push(fromStore(cursor.value as StoredRow));
        cursor.continue();
      };
      // Whatever was collected before the failure is still worth showing.
      request.onerror = () => resolve(rows);
    } catch (error) {
      warn("boot", "could not read stored captures", error);
      resolve(rows);
    }
  });
}

/** Delete one capture. Resolves once the transaction is done, best-effort. */
export async function deleteCapture(id: number): Promise<void> {
  if (id < 0) {
    sessionRows = sessionRows.filter((row) => row.id !== id);
    return;
  }
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch (error) {
      warn("boot", "could not delete a capture", error);
      resolve();
    }
  });
}

/** Delete every capture, persisted and session-only alike. */
export async function clearCaptures(): Promise<void> {
  sessionRows = [];
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch (error) {
      warn("boot", "could not clear the captures", error);
      resolve();
    }
  });
}

function pruneCaptures(store: IDBObjectStore): void {
  const count = store.count();
  count.onsuccess = () => {
    let excess = count.result - MAX_CAPTURES;
    if (excess <= 0) return;
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor || excess <= 0) return;
      cursor.delete();
      excess--;
      cursor.continue();
    };
  };
}

/** Restore a discarded keepsake for undo, retaining its id and the history's cap. */
export async function restoreCapture(row: StoredCapture): Promise<boolean> {
  if (row.id < 0) {
    sessionRows = [row, ...sessionRows.filter((item) => item.id !== row.id)]
      .sort((first, second) => second.at - first.at)
      .slice(0, MAX_SESSION_CAPTURES);
    return true;
  }
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      store.put(row);
      pruneCaptures(store);
      tx.oncomplete = () => resolve(true);
      tx.onerror = tx.onabort = () => resolve(false);
    } catch (error) {
      warn("boot", "could not restore a capture", error);
      resolve(false);
    }
  });
}

/**
 * Persist one capture and prune the store to MAX_CAPTURES. Resolves with the row's id —
 * the handle {@link resolveCapture} and {@link deleteCapture} take — which is negative
 * when the row could only be kept in memory.
 *
 * The write is *watched*, not fired and forgotten. Safari in private browsing opens the
 * database quite happily and then refuses the write — a zero quota, or a Blob it will not
 * clone — and an unobserved `put` fails without a sound: the scan looks perfect, the
 * history stays empty, and nothing anywhere says why. A refused write keeps the capture
 * in memory instead, so the tab's own scans are still there to look at.
 */
export async function saveCapture(meta: CaptureMeta, blob: Blob): Promise<number> {
  const db = await openDb();
  if (!db) return keepInSession(meta, blob);
  return new Promise((resolve) => {
    // Both `error` and `abort` fire for one failed put — the request's error bubbles to
    // the transaction and then aborts it — so the fallback is armed once or not at all.
    let settled = false;
    let id: number | null = null;
    const failed = (error: unknown): void => {
      if (settled) return;
      settled = true;
      refused("this browser refused to store a scan", error);
      resolve(keepInSession(meta, blob));
    };
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const put = store.put({ ...meta, blob, scannedAs: null });
      put.onsuccess = () => {
        id = put.result as number;
      };
      pruneCaptures(store);
      tx.oncomplete = () => {
        if (id == null) {
          // A completed transaction means the put succeeded, so this cannot happen — but
          // a row nobody can address again is better kept where it can be.
          failed(new Error("the store returned no key"));
          return;
        }
        settled = true;
        resolve(id);
      };
      tx.onerror = () => failed(tx.error);
      tx.onabort = () => failed(tx.error);
    } catch (error) {
      failed(error);
    }
  });
}

/**
 * Rewrite one row in place, persisted or session-only. Resolves false when the row is
 * gone — pruned, deleted, or never stored — or the browser would not write it.
 */
async function updateCapture(id: number, change: (row: StoredCapture) => void): Promise<boolean> {
  if (id < 0) {
    const index = sessionRows.findIndex((row) => row.id === id);
    if (index < 0) return false;
    // A fresh object: a view holding the old row from `loadCaptures` keeps what it read.
    const row = { ...sessionRows[index] };
    change(row);
    sessionRows[index] = row;
    return true;
  }
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    let found = false;
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const get = store.get(id);
      get.onsuccess = () => {
        const raw = get.result as StoredRow | undefined;
        if (!raw) return;
        const row = fromStore(raw);
        change(row);
        store.put(row);
        found = true;
      };
      tx.oncomplete = () => resolve(found);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (error) {
      warn("boot", "could not update a capture", error);
      resolve(false);
    }
  });
}

/**
 * Record the user's choice of printing for a capture — the answer to an ambiguous read.
 *
 * The row takes the chosen printing's identity and the verdict RESOLVED; what the
 * scanner had named stays on it as `scannedAs`, set the first time only, so a second
 * choice still points back at the read. Choosing the scanner's own leader is a valid
 * answer — it is then a confirmation, and the row says so the same way.
 */
export function resolveCapture(
  id: number,
  printing: Pick<CaptureIdentity, "name" | "setCode" | "collector">,
  chosenPrinting?: Printing,
  by: ResolvedBy = "user",
): Promise<boolean> {
  return updateCapture(id, (row) => {
    row.scannedAs ??= { ...identity(row), verdict: row.verdict };
    Object.assign(row, identity(printing));
    row.verdict = "RESOLVED";
    row.chosenPrinting = chosenPrinting ? { ...chosenPrinting } : null;
    row.resolvedBy = by;
  });
}

/** Take a choice back: the row is the scanner's read again, and `scannedAs` is cleared. */
export function unresolveCapture(id: number): Promise<boolean> {
  return updateCapture(id, (row) => {
    if (!row.scannedAs) return;
    const { verdict, ...read } = row.scannedAs;
    Object.assign(row, read);
    row.verdict = verdict;
    row.scannedAs = null;
    row.chosenPrinting = null;
    row.resolvedBy = null;
  });
}

/** Sync current scanner evidence without replacing media or resurrecting a pruned row. */
export function updateCaptureEvidence(
  id: number,
  printing: Printing,
  verdict: "ACCEPTED" | "AMBIGUOUS",
  candidates: Candidate[],
): Promise<boolean> {
  return updateCapture(id, (row) => {
    Object.assign(row, identity(printing));
    row.verdict = verdict;
    row.illustration = printing.illustration;
    row.candidates = shortlist(candidates);
    row.scannedAs = null;
    row.chosenPrinting = null;
    row.resolvedBy = null;
  });
}
